/**
 * Service Worker registration and management utilities
 */

const isProduction = process.env.NODE_ENV === 'production'
const isClient = typeof window !== 'undefined'

/**
 * Register the service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!isClient || !isProduction) {
        return null
    }

    if ('serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.register('/sw.js', {
                scope: '/'
            })

            console.log('Service Worker registered successfully:', registration)

            // Handle updates
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing
                if (newWorker) {
                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            // New content is available, show update notification
                            showUpdateNotification()
                        }
                    })
                }
            })

            return registration
        } catch (error) {
            console.error('Service Worker registration failed:', error)
            return null
        }
    }

    return null
}

/**
 * Unregister the service worker
 */
export async function unregisterServiceWorker(): Promise<boolean> {
    if (!isClient) {
        return false
    }

    if ('serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.ready
            const unregistered = await registration.unregister()
            console.log('Service Worker unregistered:', unregistered)
            return unregistered
        } catch (error) {
            console.error('Service Worker unregistration failed:', error)
            return false
        }
    }

    return false
}

/**
 * Check if the service worker is supported and registered
 */
export function isServiceWorkerSupported(): boolean {
    return isClient && 'serviceWorker' in navigator
}

/**
 * Get the current service worker registration
 */
export async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (!isServiceWorkerSupported()) {
        return null
    }

    try {
        return await navigator.serviceWorker.ready
    } catch (error) {
        console.error('Failed to get service worker registration:', error)
        return null
    }
}

/**
 * Show update notification when new content is available
 */
function showUpdateNotification(): void {
    // Create a simple notification
    const notification = document.createElement('div')
    notification.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      right: 20px;
      background: #1f2937;
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 9999;
      max-width: 300px;
      font-family: system-ui, -apple-system, sans-serif;
    ">
      <div style="font-weight: 600; margin-bottom: 8px;">
        🚀 New content available!
      </div>
      <div style="font-size: 14px; margin-bottom: 12px; opacity: 0.9;">
        A new version of the site is ready.
      </div>
      <button onclick="window.location.reload()" style="
        background: #3b82f6;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        margin-right: 8px;
      ">
        Refresh
      </button>
      <button onclick="this.parentElement.parentElement.remove()" style="
        background: transparent;
        color: #9ca3af;
        border: 1px solid #374151;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
      ">
        Later
      </button>
    </div>
  `

    document.body.appendChild(notification)

    // Auto-remove after 10 seconds
    setTimeout(() => {
        if (notification.parentElement) {
            notification.remove()
        }
    }, 10000)
}

/**
 * Handle offline/online status
 */
export function setupConnectionHandlers(): void {
    if (!isClient) return

    const showConnectionStatus = (isOnline: boolean) => {
        const existingStatus = document.getElementById('connection-status')
        if (existingStatus) {
            existingStatus.remove()
        }

        if (!isOnline) {
            const statusBar = document.createElement('div')
            statusBar.id = 'connection-status'
            statusBar.innerHTML = `
        <div style="
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: #ef4444;
          color: white;
          text-align: center;
          padding: 8px;
          font-size: 14px;
          z-index: 10000;
          font-family: system-ui, -apple-system, sans-serif;
        ">
          📡 You're offline. Some features may not work.
        </div>
      `
            document.body.appendChild(statusBar)
        }
    }

    // Initial status
    showConnectionStatus(navigator.onLine)

    // Listen for connection changes
    window.addEventListener('online', () => {
        showConnectionStatus(true)
        console.log('Connection restored')
    })

    window.addEventListener('offline', () => {
        showConnectionStatus(false)
        console.log('Connection lost')
    })
}

/**
 * Cache important resources
 */
export async function cacheImportantResources(urls: string[]): Promise<void> {
    if (!isServiceWorkerSupported()) {
        return
    }

    try {
        const cache = await caches.open('important-resources')
        await cache.addAll(urls)
        console.log('Important resources cached:', urls)
    } catch (error) {
        console.error('Failed to cache important resources:', error)
    }
}

/**
 * Clear all caches
 */
export async function clearAllCaches(): Promise<void> {
    if (!isClient || !('caches' in window)) {
        return
    }

    try {
        const cacheNames = await caches.keys()
        await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
        )
        console.log('All caches cleared')
    } catch (error) {
        console.error('Failed to clear caches:', error)
    }
}

/**
 * Get cache storage usage
 */
export async function getCacheStorageUsage(): Promise<{ used: number; quota: number } | null> {
    if (!isClient || !('storage' in navigator) || !('estimate' in navigator.storage)) {
        return null
    }

    try {
        const estimate = await navigator.storage.estimate()
        return {
            used: estimate.usage || 0,
            quota: estimate.quota || 0
        }
    } catch (error) {
        console.error('Failed to get storage estimate:', error)
        return null
    }
}